import * as React from 'react';
import { observer } from 'mobx-react';
import { inject } from '../../utils';
import { withTheme } from 'styled-components';
import { styled } from '../../utils/styled';
import { propStyles } from '../../utils/propStyles';
import { isDisabled } from '../../utils/customPropStyles';
import { translate } from '../Translate';
import Icon from '../Icon';
import {
    DataStore,
    TranslationStore,
    AccessibilityStore,
    QueryStore,
    ScreenSizeStore,
    FeedbackStore,
    EntityStore,
} from '../../stores';
import { action, computed, observable, isArrayLike } from 'mobx';
import { bounceRule } from './DownloadControls';
import { ShareClassDocument } from '../../models/entity';
import {
    CopyUrlActionOptions,
    DownloadControlActions,
} from '../../models/configuration/explorer/DownloadControls';
import {
    getOptionLabel,
    isOptionLabelHidden,
} from '../../utils/document/downloadControls';
import { Buttons } from '../../models/configuration/accessibility/buttons';
import { FeedbackType } from '../Feedback/Models/FeedbackType';
import { Theme } from '../../models/Theme';

const ButtonIcon = styled<any, any>(
    Icon,
    'buttonIcon',
    'copyDocumentUrlButton'
)``;

ButtonIcon.defaultProps = {
    marginX: 'sm',
    height: '1.6em',
    width: '1.6em',
};

const Button = styled<any, any>('button', 'button', 'copyDocumentUrlButton')`
    ${propStyles};
    ${isDisabled};
    &:hover {
        ${ButtonIcon} {
            animation-name: ${bounceRule};
            animation-duration: 1s;
            animation-fill-mode: both;
            transform-origin: center bottom;
        }
    }
`;

Button.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    paddingX: '0',
    paddingY: '0',
    fontSize: 'inherit',
    fontFamily: 'body',
    fontWeight: 'regular',
    textAlign: 'left',
    color: 'inherit',
    bgColor: 'transparent',
    borderStyle: 'none',
    cursor: 'pointer',
    hover: {
        color: 'primary',
    },
    justifyContent: 'flex-end',
    flex: '0 0 auto',
};

interface CopyDocumentUrlButtonProps {
    documentDisplayName: string;
    fileName?: string;
    metricContext?: string;
    document: ShareClassDocument | Array<ShareClassDocument>;
    actions: DownloadControlActions;
    options: CopyUrlActionOptions;
    dataStore?: DataStore;
    translationStore?: TranslationStore;
    accessibilityStore?: AccessibilityStore;
    theme?: Theme;
    queryStore?: QueryStore;
    screenSizeStore?: ScreenSizeStore;
    feedbackStore?: FeedbackStore;
    entityStore?: EntityStore;
}

@inject(
    'dataStore',
    'accessibilityStore',
    'translationStore',
    'screenSizeStore',
    'queryStore',
    'feedbackStore',
    'entityStore'
)
@observer
class CopyDocumentUrlButton extends React.Component<
    CopyDocumentUrlButtonProps
> {
    @observable isCopied: boolean = false;

    @computed
    get showLabel(): boolean {
        const { actions, options, screenSizeStore } = this.props;
        return !isOptionLabelHidden({
            screenSizeStore,
            downloadControlActions: actions,
            labelOptions: options.labelOptions,
        });
    }

    @computed
    get label(): string {
        const {
            entityStore,
            translationStore,
            queryStore,
            screenSizeStore,
        } = this.props;

        if (this.isCopied) {
            return getOptionLabel({
                defaultLabel: translate(translationStore, 'Copied'),
                document,
                entityStore,
                queryStore,
                screenSizeStore,
                labelKey: 'successLabel',
                downloadControlActions: this.props.actions,
                labelOptions: this.props.options.labelOptions,
            });
        }

        return getOptionLabel({
            document,
            entityStore,
            queryStore,
            screenSizeStore,
            labelKey: 'label',
            downloadControlActions: this.props.actions,
            labelOptions: this.props.options.labelOptions,
        });
    }

    @computed
    get showIcon(): boolean {
        const hide = this.props.options.iconOptions?.hide || false;
        return !hide;
    }

    @computed
    get icon(): React.ReactNode {
        const { theme, options } = this.props;

        const filename = this.isDisabled
            ? options.iconOptions?.disabledFilename
            : options.iconOptions?.filename ||
              'DownloadControlsCopyDocumentUrlIcon';

        if (this.isCopied) {
            const successFilename =
                options.iconOptions?.successFilename ||
                'DownloadControlsCopyDocumentUrlSuccessIcon';

            return (
                <ButtonIcon
                    aria-label={this.ariaLabel(
                        'DownloadControlsCopyDocumentUrlSuccessIcon'
                    )}
                    fileName={successFilename}
                    {...theme.DownloadCopyUrlSuccessIcon}
                />
            );
        }

        return (
            <ButtonIcon
                aria-label={this.ariaLabel(
                    'DownloadControlsCopyDocumentUrlIcon'
                )}
                fileName={filename}
                {...theme.DownloadCopyUrlIcon}
            />
        );
    }

    @computed
    get documentsArray(): Array<ShareClassDocument> {
        const { document = [] } = this.props;

        if (isArrayLike(document)) {
            return document;
        }

        return [document];
    }

    @computed
    get isDisabled(): boolean {
        return this.documentsArray.length === 0;
    }

    @computed
    get documentUrls(): string {
        return this.documentsArray
            .map(document => this.getDocumentUrl(document))
            .join('\n');
    }

    getDocumentUrl = (document): string => {
        const { dataStore, entityStore, options } = this.props;
        const { getDocumentLink } = dataStore;
        const urlQuery = options.url;

        return getDocumentLink([document], entityStore?.value, urlQuery);
    };

    @action
    onClick = async event => {
        event.stopPropagation();
        const { options, entityStore } = this.props;
        const { feedback } = options;
        const feedbackStore: FeedbackStore = this.props.feedbackStore;
        try {
            await navigator.clipboard.writeText(this.documentUrls);
            this.isCopied = true;
            setTimeout(() => {
                this.isCopied = false;
            }, 600);
            feedbackStore.addMessage({
                text: this.ariaLabelText,
                feedbackType: FeedbackType.success,
                config: feedback,
                queryContext: {
                    entityByType: {
                        [feedback?.textQuery?.entityType]: {
                            ...entityStore?.value,
                        },
                    },
                    document: (this.documentsArray.length === 1
                        ? this.documentsArray[0]
                        : undefined) as any,
                    response: {
                        url: this.documentUrls,
                    },
                },
            });
        } catch (error) {
            console.error('Unable to copy document URL', error);
            feedbackStore.addMessage({
                text:
                    'Unable to copy document URL, please contact the administrator',
                feedbackType: FeedbackType.error,
                config: options.feedback,
            });
        }
    };

    ariaLabel = (
        key: keyof Buttons,
        defaultValue?: string,
        placeholders?: object
    ): string => {
        const { accessibilityStore } = this.props;
        const ariaLabel = accessibilityStore.buttonAccessibilityValue(
            key,
            this.label || defaultValue,
            placeholders
        );
        return ariaLabel;
    };

    @computed
    get ariaLabelText(): string {
        const { documentDisplayName } = this.props;

        return this.isCopied
            ? this.ariaLabel(
                  'CopyDocumentUrlButton',
                  'Copied document URL for {documentDisplayName}',
                  { documentDisplayName }
              )
            : this.ariaLabel(
                  'CopyDocumentUrlSuccessButton',
                  'Copy document URL for {documentDisplayName}',
                  { documentDisplayName }
              );
    }

    render() {
        const {
            documentDisplayName,
            fileName,
            metricContext,
            translationStore,
            accessibilityStore,
            theme,
            ...props
        } = this.props;

        return (
            <Button
                title={this.ariaLabelText}
                aria-label={this.ariaLabelText}
                disabled={this.isDisabled}
                // Used for Adobe analytics tracking
                data-metrics-link-type={'copyDocumentUrl'}
                data-metrics-link-dest={fileName}
                data-metric-id={`downloadControl-copyDocumentUrl-${metricContext}`}
                onClick={event => this.onClick(event)}
                {...theme.DownloadCopyUrlButton}
                {...props}
            >
                {this.showLabel && this.label}
                {this.showIcon && this.icon}
            </Button>
        );
    }
}

export default withTheme(CopyDocumentUrlButton);



// WEBPACK FOOTER //
// ./src/Components/Document/CopyDocumentUrlButton.tsx